import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  billingErrorCode,
  billingErrorSessionId,
  createCheckoutSession,
  type CheckoutRequest,
} from "@/services/api/billingService"
import { markUpgradeLinkOpened } from "@/utils/upgradeSession"
import { CheckoutElementsProvider } from "@stripe/react-stripe-js/checkout"
import {
  loadStripe,
  type Stripe,
  type StripeElementLocale,
} from "@stripe/stripe-js"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { checkoutAppearance } from "./checkoutAppearance"
import { CheckoutForm } from "./CheckoutForm"

const STRIPE_LOCALES: Record<string, StripeElementLocale> = {
  DE: "de",
  EN: "en",
  JA: "ja",
}

// loadStripe once per key and language: a new call per render would reload Stripe.js,
// and the language is fixed when Stripe.js loads.
const stripeByKey = new Map<string, Promise<Stripe | null>>()
const stripeFor = (
  publishableKey: string,
  locale: StripeElementLocale,
): Promise<Stripe | null> => {
  const cacheKey = `${publishableKey}:${locale}`
  let stripe = stripeByKey.get(cacheKey)
  if (!stripe) {
    stripe = loadStripe(publishableKey, { locale }).catch((error: unknown) => {
      // A failed load (blocked script, offline) must not stick: the next checkout loads again
      stripeByKey.delete(cacheKey)
      throw error
    })
    stripeByKey.set(cacheKey, stripe)
  }
  return stripe
}

interface CheckoutStepProps {
  publishableKey: string
  request: CheckoutRequest
  onBack: () => void
  onComplete: (sessionId: string) => void
  onAlreadySubscribed: () => void
  /** The stage stopped selling (503 BILLING_NOT_CONFIGURED). */
  onNotConfigured: () => void
  onError: () => void
  onPayingChange: (paying: boolean) => void
}

/** The checkout for one plan and interval: our form around Stripe's payment fields (Checkout Elements). */
export const CheckoutStep: React.FC<CheckoutStepProps> = ({
  publishableKey,
  request,
  onBack,
  onComplete,
  onAlreadySubscribed,
  onNotConfigured,
  onError,
  onPayingChange,
}) => {
  const { currentLanguage } = useTranslation()
  const locale = STRIPE_LOCALES[currentLanguage] ?? "auto"
  const stripe = useMemo(
    () => stripeFor(publishableKey, locale),
    [publishableKey, locale],
  )
  // The session id the server returned, for the success flow after confirm.
  const sessionIdRef = useRef("")
  // The provider needs stable options, so callbacks go through refs.
  const callbacks = useRef({
    onComplete,
    onAlreadySubscribed,
    onNotConfigured,
    onError,
  })
  useEffect(() => {
    callbacks.current = {
      onComplete,
      onAlreadySubscribed,
      onNotConfigured,
      onError,
    }
  })
  const { plan, interval } = request

  // The provider takes the client secret as a promise; the request runs in the
  // effect below, which settles it. A new plan or interval remounts this step.
  const [clientSecret] = useState(() => {
    let resolve: (secret: string) => void = () => undefined
    const promise = new Promise<string>((settle) => {
      resolve = settle
    })
    return { promise, resolve }
  })
  useEffect(() => {
    // StrictMode runs this twice; the idempotency key gives both the same session.
    let current = true
    createCheckoutSession({ plan, interval }).then(
      (session) => {
        if (!current) return
        sessionIdRef.current = session.sessionId
        // The paid group can arrive any moment from here on
        markUpgradeLinkOpened()
        clientSecret.resolve(session.clientSecret)
      },
      (error: unknown) => {
        if (!current) return
        // Stripe's provider does not catch a rejection: the error panel
        // replaces the checkout, so the secret just never settles.
        const code = billingErrorCode(error)
        const paidSessionId = billingErrorSessionId(error)
        if (code === "PAYMENT_IN_PROGRESS" && paidSessionId) {
          // Paid in another tab, not activated yet: show that session's activation
          markUpgradeLinkOpened()
          callbacks.current.onComplete(paidSessionId)
        } else if (code === "ALREADY_SUBSCRIBED") {
          callbacks.current.onAlreadySubscribed()
        } else if (code === "BILLING_NOT_CONFIGURED") {
          callbacks.current.onNotConfigured()
        } else {
          callbacks.current.onError()
        }
      },
    )
    return () => {
      current = false
    }
  }, [plan, interval, clientSecret])
  const options = useMemo(
    () => ({
      clientSecret: clientSecret.promise,
      elementsOptions: { appearance: checkoutAppearance() },
    }),
    [clientSecret],
  )

  const handlePaid = useCallback(
    () => callbacks.current.onComplete(sessionIdRef.current),
    [],
  )
  const handleError = useCallback(() => callbacks.current.onError(), [])

  return (
    // The provider keeps the Stripe instance it started with: a new language needs a new provider
    <CheckoutElementsProvider key={locale} stripe={stripe} options={options}>
      <CheckoutForm
        plan={plan}
        onBack={onBack}
        onPaid={handlePaid}
        onError={handleError}
        onPayingChange={onPayingChange}
      />
    </CheckoutElementsProvider>
  )
}
